import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import { API_BASE } from './config';
import socket from '../socket/socket';
import '../css/RandomMatchPanel.css';

const MODES = [
  {
    value: 'PICK_ONE',
    label: 'Pick One',
    description: 'ล็อกผู้เล่นหนึ่งคนแล้วหาอีกสามคน'
  },
  {
    value: 'AI_RANDOM',
    label: 'AI RANDOM',
    description: 'มาก่อนได้ก่อน เลือกคะแนนรายบุคคลใกล้กัน แล้วจัดทีมให้สูสี'
  },
  {
    value: 'MANUAL_4',
    label: 'Select 4',
    description: 'เลือก Team 1 สองคน และ Team 2 สองคน'
  }
];

const AUTOMATIC_MODES = new Set(['AI_RANDOM']);
const RECOMMENDATION_VISIBLE_STORAGE_KEY =
  'badminton-match-recommendation-visible';

const POINT_RANGES = [100, 200, 300];
const RANK_ORDER = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const PICK_ONE_STATUS_ORDER = ['ACTIVE', 'QUEUE', 'PLAY', 'HOLD', 'CHECKOUT'];

const PLAY_STYLE_OPTIONS = [
  {
    value: 'ANY',
    icon: '🏸',
    label: 'ทุก Mood',
    description: 'ไม่กรอง Mood ของวันนี้'
  },
  {
    value: 'FAST_POWER_MIX',
    icon: '⚡',
    label: 'ทั่วไป',
    description: 'เร็ว หนัก สลับ เบา'
  },
  {
    value: 'TIGHT_MEDIUM_HEAVY',
    icon: '🎯',
    label: 'ขอเหนื่อย',
    description: 'ตึง ปานกลาง–หนัก'
  },
  {
    value: 'ALL_ROUND_READY',
    icon: '🔥',
    label: 'ได้หมด พร้อมชน',
    description: 'พร้อมเล่นทุกรูปแบบ'
  },
  {
    value: 'ENJOY_FUN',
    icon: '😄',
    label: 'เน้นฮาและเหนื่อย',
    description: 'Enjoy เฮฮา ไม่เครียด'
  }
];

function getPlayStyleLabel(value) {
  return PLAY_STYLE_OPTIONS.find(option => option.value === value)?.label || '-';
}

const PLAY_STYLE_ALIASES = new Map([
  ['FAST_POWER_MIX', 'FAST_POWER_MIX'],
  ['เน้นเร็ว หนัก สลับ เบา (ตามระดับมือ)', 'FAST_POWER_MIX'],
  ['เร็ว หนัก สลับ เบา', 'FAST_POWER_MIX'],
  ['TIGHT_MEDIUM_HEAVY', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง–หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง - หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['ตึง ปานกลาง–หนัก', 'TIGHT_MEDIUM_HEAVY'],
  ['ALL_ROUND_READY', 'ALL_ROUND_READY'],
  ['ได้หมด พร้อมชน', 'ALL_ROUND_READY'],
  ['ENJOY_FUN', 'ENJOY_FUN'],
  ['เน้น Enjoy เฮฮา ไม่เครียด (ตามระดับมือ)', 'ENJOY_FUN'],
  ['Enjoy เฮฮา', 'ENJOY_FUN']
]);

function normalizePlayStyleValue(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return null;

  return (
    PLAY_STYLE_ALIASES.get(rawValue) ||
    PLAY_STYLE_ALIASES.get(rawValue.toUpperCase()) ||
    null
  );
}

function getStatus(player) {
  return String(player?.memberStatus ?? player?.status ?? 'ACTIVE').toUpperCase();
}

function getPickOneStatusPriority(player) {
  const index = PICK_ONE_STATUS_ORDER.indexOf(getStatus(player));
  return index === -1 ? PICK_ONE_STATUS_ORDER.length : index;
}

function getPlayerGames(player) {
  return Number(
    player?.totalMatches ??
    player?.total_matches ??
    player?.total_games ??
    0
  ) || 0;
}

function normalizePlayerRank(value, point = 0) {
  const rawRank = String(value || '').trim();
  const rankWithoutPrefix = rawRank.startsWith('มือ')
    ? rawRank.slice('มือ'.length).trim()
    : rawRank;
  const normalized = rankWithoutPrefix
    .toUpperCase()
    .replace(/^SP$/, 'S/P')
    .replace(/^S-P$/, 'S/P')
    .replace(/^NP$/, 'N/S')
    .replace(/^N-S$/, 'N/S');

  if (RANK_ORDER.includes(normalized)) return normalized;

  const safePoint = Number(point || 0);
  if (safePoint < 350) return 'BG';
  if (safePoint < 400) return 'BGM';
  if (safePoint < 450) return 'NB';
  if (safePoint < 500) return 'NB+';
  if (safePoint < 550) return 'N-';
  if (safePoint < 600) return 'N';
  if (safePoint < 650) return 'N/S';
  if (safePoint < 700) return 'S';
  return 'S/P';
}

function getRankDistance(left, right) {
  const leftRank = normalizePlayerRank(left?.level_rank, left?.point);
  const rightRank = normalizePlayerRank(right?.level_rank, right?.point);
  return Math.abs(RANK_ORDER.indexOf(leftRank) - RANK_ORDER.indexOf(rightRank));
}

function parsePlayerTime(value) {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function getQueueStartedAt(player) {
  return parsePlayerTime(
    player?.last_finished_at ||
    player?.active_since ||
    player?.checkin_time
  );
}

function getArrivalTime(player) {
  return parsePlayerTime(
    player?.active_since ||
    player?.checkin_time ||
    player?.last_finished_at
  );
}

function comparePlayersByPriority(left, right, priority) {
  if (priority === 'LEAST_GAMES') {
    const gameDiff = getPlayerGames(left) - getPlayerGames(right);
    if (gameDiff !== 0) return gameDiff;
  }

  if (priority === 'WAIT_LONGEST') {
    const waitDiff = getQueueStartedAt(left) - getQueueStartedAt(right);
    if (waitDiff !== 0) return waitDiff;
  }

  const arrivalDiff = getArrivalTime(left) - getArrivalTime(right);
  if (arrivalDiff !== 0) return arrivalDiff;

  return Number(left.user_id) - Number(right.user_id);
}

function formatWaitTime(player, now) {
  const startedAt = getQueueStartedAt(player);
  if (startedAt === Number.MAX_SAFE_INTEGER) return '-';

  const minutes = Math.max(0, Math.floor((now - startedAt) / 60000));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) return `${hours} ชม. ${remainingMinutes} นาที`;
  return `${minutes} นาที`;
}

function getRelationCount(relationMap, leftPlayer, rightPlayer) {
  const leftId = Number(leftPlayer?.user_id);
  const rightId = Number(rightPlayer?.user_id);

  if (!Number.isInteger(leftId) || !Number.isInteger(rightId)) return 0;

  return Number(
    relationMap?.[`${leftId}-${rightId}`] ??
    relationMap?.[`${rightId}-${leftId}`] ??
    0
  ) || 0;
}

async function postMatchmakingJson(path, payload) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    const requestError = new Error(
      responseData.message ||
      responseData.error ||
      `HTTP ${response.status}`
    );
    requestError.data = responseData;
    requestError.status = response.status;
    throw requestError;
  }

  return responseData;
}

function RandomMatchPanel({
  groupId,
  allPlayers = [],
  lockedPairs = [],
  pairMap = {},
  opponentMap = {},
  onCreated,
  displayMode = 'full'
}) {
  const isMonitorAi = displayMode === 'monitor-ai';
  const [mode, setMode] = useState('PICK_ONE');
  const [priority, setPriority] = useState('WAIT_LONGEST');
  const [pointRange, setPointRange] = useState(100);
  const [playStyle, setPlayStyle] = useState('ANY');
  const [matchCount, setMatchCount] = useState(1);
  const [approvePlaying, setApprovePlaying] = useState(false);
  const [priorityKeyword, setPriorityKeyword] = useState('');
  const [pickedPlayer, setPickedPlayer] = useState(null);
  const [pickOnePlayers, setPickOnePlayers] = useState([]);
  const [manualPlayers, setManualPlayers] = useState([
    null,
    null,
    null,
    null
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [dailyStyleByUserId, setDailyStyleByUserId] = useState({});
  const [aiAutoEnabled, setAiAutoEnabled] = useState(false);
  const [aiAutoLoading, setAiAutoLoading] = useState(false);
  const [aiAutomationStatus, setAiAutomationStatus] = useState('');
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiAutomationCycle, setAiAutomationCycle] = useState(0);
  const [expandedInfoPanel, setExpandedInfoPanel] = useState(null);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [statusSearchKeyword, setStatusSearchKeyword] = useState('');
  const [nearbySearchKeyword, setNearbySearchKeyword] = useState('');
  const [nearbyGameSort, setNearbyGameSort] = useState('ASC');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState(null);
  const [recommendationVisible, setRecommendationVisible] = useState(() => {
    try {
      return window.localStorage.getItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY
      ) !== 'false';
    } catch {
      return true;
    }
  });
  const aiRequestInFlightRef = useRef(false);
  const aiProcessedSignatureRef = useRef('');
  const pickOneAttemptedIdsRef = useRef(new Set());
  const onCreatedRef = useRef(onCreated);

  const canApprovePlaying = mode === 'PICK_ONE' || mode === 'MANUAL_4';
  const usesManualSelection = mode === 'MANUAL_4';
  const supportsMultipleMatches = mode === 'AI_RANDOM';
  const usesIndividualPointRange = mode === 'PICK_ONE';
  const usesPointRange = usesIndividualPointRange;

  useEffect(() => {
    onCreatedRef.current = onCreated;
  }, [onCreated]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY,
        String(recommendationVisible)
      );
    } catch {
      // หาก localStorage ใช้ไม่ได้ ปุ่มยังเปิด/ปิดได้ในรอบปัจจุบัน
    }
  }, [recommendationVisible]);

  const loadDailyStyles = useCallback(async () => {
    if (!groupId) {
      setDailyStyleByUserId({});
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/users/daily-play-style/${groupId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      const nextStyleMap = {};

      rows.forEach(row => {
        const userId = Number(row.user_id);
        const style = normalizePlayStyleValue(
          row.daily_play_style ?? row.play_style
        );

        if (Number.isInteger(userId) && userId > 0 && style) {
          nextStyleMap[userId] = style;
        }
      });

      setDailyStyleByUserId(nextStyleMap);
    } catch (loadError) {
      console.error('LOAD DAILY PLAY STYLES ERROR:', loadError);
    }
  }, [groupId]);

useEffect(() => {
  if (!groupId) return undefined;

  const isCurrentGroupEvent = (payload = {}) => {
    const eventGroupId =
      payload.groupId ??
      payload.group_id;

    // ถ้า Backend ส่ง groupId มา ให้ตรวจว่าเป็น Group ปัจจุบัน
    if (eventGroupId !== undefined && eventGroupId !== null) {
      return Number(eventGroupId) === Number(groupId);
    }

    // รองรับกรณี Backend ส่ง groupIds เป็น array
    const eventGroupIds = Array.isArray(payload.groupIds)
      ? payload.groupIds.map(Number)
      : [];

    // ถ้า event ไม่มี groupId ให้ refresh Group ปัจจุบัน
    return (
      eventGroupIds.length === 0 ||
      eventGroupIds.includes(Number(groupId))
    );
  };

  const refreshPlayers = async (payload = {}) => {
    if (!isCurrentGroupEvent(payload)) return;

    // โหลด Mood ใหม่
    loadDailyStyles().catch(error => {
      console.error(
        'Realtime daily style refresh error:',
        error
      );
    });

    // เรียก Parent ให้โหลด allPlayers ใหม่
    if (onCreatedRef.current) {
      try {
        await onCreatedRef.current(payload);
      } catch (error) {
        console.error(
          'Realtime players refresh error:',
          error
        );
      }
    }
  };

  const joinGroupAndRefresh = () => {
    console.log(
      '🔌 MATCHMAKING JOIN GROUP:',
      Number(groupId)
    );

    // สำคัญ: ต้องเข้า Socket room ของ Group
    socket.emit(
      'join-group',
      Number(groupId)
    );

    // โหลดข้อมูลทันทีหลัง connect
    loadDailyStyles().catch(error => {
      console.error(
        'Initial daily style refresh error:',
        error
      );
    });

    if (onCreatedRef.current) {
      Promise.resolve(
        onCreatedRef.current()
      ).catch(error => {
        console.error(
          'Initial players refresh error:',
          error
        );
      });
    }
  };

  // Socket reconnect
  socket.on(
    'connect',
    joinGroupAndRefresh
  );

  // ถ้า Socket connect อยู่แล้ว
  if (socket.connected) {
    joinGroupAndRefresh();
  }

  // =====================================
  // REALTIME PLAYER EVENTS
  // =====================================

  socket.on(
    'player-added',
    refreshPlayers
  );

  socket.on(
    'member-joined',
    refreshPlayers
  );

  socket.on(
    'member-updated',
    refreshPlayers
  );

  socket.on(
    'player-style-updated',
    refreshPlayers
  );

  // สำคัญสำหรับ ACTIVE / QUEUE / PLAY
  socket.on(
    'player-status-updated',
    refreshPlayers
  );

  // สำคัญสำหรับข้อมูลผู้เล่นที่เปลี่ยน
  socket.on(
    'player-updated',
    refreshPlayers
  );

  // =====================================
  // POLLING BACKUP
  // =====================================

  const pollingId = window.setInterval(
    () => {
      loadDailyStyles().catch(() => {});

      if (onCreatedRef.current) {
        Promise.resolve(
          onCreatedRef.current()
        ).catch(() => {});
      }
    },
    5000
  );

  // เมื่อกลับมาหน้า Browser
  const handleFocus = () => {
    socket.emit(
      'join-group',
      Number(groupId)
    );

    refreshPlayers({
      groupId: Number(groupId)
    });
  };

  window.addEventListener(
    'focus',
    handleFocus
  );

  return () => {
    window.clearInterval(pollingId);

    window.removeEventListener(
      'focus',
      handleFocus
    );

    socket.off(
      'connect',
      joinGroupAndRefresh
    );

    socket.off(
      'player-added',
      refreshPlayers
    );

    socket.off(
      'member-joined',
      refreshPlayers
    );

    socket.off(
      'member-updated',
      refreshPlayers
    );

    socket.off(
      'player-style-updated',
      refreshPlayers
    );

    socket.off(
      'player-status-updated',
      refreshPlayers
    );

    socket.off(
      'player-updated',
      refreshPlayers
    );
  };
}, [
  groupId,
  loadDailyStyles
]);

  const normalizedPlayers = useMemo(
    () => allPlayers.map(player => {
      const userId = Number(player.user_id);
      const dailyPlayStyle = normalizePlayStyleValue(
        dailyStyleByUserId[userId] ??
        player.daily_play_style ??
        player.play_style ??
        player.dailyPlayStyle
      );

      return {
        ...player,
        daily_play_style: dailyPlayStyle,
        status: getStatus(player),
        memberStatus: getStatus(player)
      };
    }),
    [allPlayers, dailyStyleByUserId]
  );

  const activeStyleCounts = useMemo(() => {
    const counts = {
      FAST_POWER_MIX: 0,
      TIGHT_MEDIUM_HEAVY: 0,
      ALL_ROUND_READY: 0,
      ENJOY_FUN: 0
    };
    let totalActive = 0;
    let missingStyle = 0;

    normalizedPlayers.forEach(player => {
      if (getStatus(player) !== 'ACTIVE') return;
      totalActive += 1;

      if (player.daily_play_style && counts[player.daily_play_style] != null) {
        counts[player.daily_play_style] += 1;
      } else {
        missingStyle += 1;
      }
    });

    return { counts, totalActive, missingStyle };
  }, [normalizedPlayers]);

  const minimumStylePlayers = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const styleMatchingPlayers = useMemo(
    () => playStyle === 'ANY'
      ? normalizedPlayers
      : normalizedPlayers.filter(
        player => player.daily_play_style === playStyle
      ),
    [normalizedPlayers, playStyle]
  );

  const styleMatchingActiveCount = useMemo(
    () => styleMatchingPlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ).length,
    [styleMatchingPlayers]
  );

  const styleEligiblePlayers = useMemo(
    () => (
      mode === 'AI_RANDOM' || playStyle === 'ANY'
        ? normalizedPlayers
        : styleMatchingPlayers
    ),
    [
      mode,
      normalizedPlayers,
      playStyle,
      styleMatchingPlayers
    ]
  );

  const activePlayers = useMemo(
    () => styleEligiblePlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ),
    [styleEligiblePlayers]
  );

  const aiOrderedActivePlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'ACTIVE')
      .sort((left, right) =>
        comparePlayersByPriority(left, right, 'FIFO')
      ),
    [normalizedPlayers]
  );

  const aiActiveSignature = useMemo(
    () => aiOrderedActivePlayers.map(player => [
      Number(player.user_id),
      Number(player.point || 0),
      player.level_rank || '',
      player.daily_play_style || '',
      player.active_since ||
        player.last_finished_at ||
        player.checkin_time ||
        ''
    ].join(':')).join('|'),
    [aiOrderedActivePlayers]
  );

  const aiLockSignature = useMemo(
    () => lockedPairs
      .map(lock => [
        Number(lock.user_a),
        Number(lock.user_b)
      ].sort((left, right) => left - right).join('-'))
      .sort()
      .join('|'),
    [lockedPairs]
  );

  const pickOneAutoSignature = mode === 'PICK_ONE'
    ? [
      Number(pickedPlayer?.user_id || 0),
      ...pickOnePlayers.map(player => Number(player.user_id)),
      pointRange,
      priority,
      approvePlaying ? 1 : 0
    ].join(':')
    : '';
  const aiMatchmakingSignature =
    `${mode}::${aiActiveSignature}::${aiLockSignature}::${matchCount}::${pickOneAutoSignature}`;

  const availableMatchCount = Math.min(
    15,
    Math.floor(activePlayers.length / 4)
  );
  const maxMatchCount =
    mode === 'AI_RANDOM'
      ? 15
      : Math.max(1, availableMatchCount);

  const requiredPlayerCount = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const playingPlayers = useMemo(
    () => styleEligiblePlayers.filter(
      player => getStatus(player) === 'PLAY'
    ),
    [styleEligiblePlayers]
  );

  const selectablePlayers = useMemo(() => {
    if (!canApprovePlaying || !approvePlaying) return activePlayers;

    // HOLD หมายถึงยังไม่พร้อม จึงไม่ถูกนำไปจัด Match แม้เปิด Approve
    return styleEligiblePlayers.filter(
      player => getStatus(player) !== 'HOLD'
    );
  }, [
    activePlayers,
    approvePlaying,
    canApprovePlaying,
    styleEligiblePlayers
  ]);

  const strictStyleAvailableCount =
    canApprovePlaying && approvePlaying
      ? selectablePlayers.length
      : activePlayers.length;
  const lacksRequiredStylePlayers =
    playStyle !== 'ANY' &&
    strictStyleAvailableCount < minimumStylePlayers;

  const orderedSelectablePlayers = useMemo(
    () => [...selectablePlayers].sort(
      (left, right) => (
        mode === 'PICK_ONE'
          ? getPickOneStatusPriority(left) - getPickOneStatusPriority(right) ||
            getPlayerGames(left) - getPlayerGames(right) ||
            comparePlayersByPriority(left, right, 'WAIT_LONGEST')
          : comparePlayersByPriority(left, right, priority)
      )
    ),
    [mode, priority, selectablePlayers]
  );

  const pickOneSelectablePlayers = useMemo(
    () => orderedSelectablePlayers,
    [orderedSelectablePlayers]
  );

  const pickOneAnchorPlayers = useMemo(
    () => pickOneSelectablePlayers,
    [pickOneSelectablePlayers]
  );

  const statusSearchResults = useMemo(() => {
    const keyword = statusSearchKeyword.trim().toLowerCase();
    if (!keyword) return [];

    return normalizedPlayers
      .filter(player =>
        String(player.name || '').toLowerCase().includes(keyword)
      )
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      )
      .slice(0, 12);
  }, [normalizedPlayers, statusSearchKeyword]);

  const heldPlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'HOLD')
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      ),
    [normalizedPlayers]
  );

  const visibleStatusPlayers = statusSearchKeyword.trim()
    ? statusSearchResults
    : heldPlayers;

  const updateAvailabilityStatus = async (player, nextStatus) => {
    const userId = Number(player?.user_id);
    if (!Number.isInteger(userId) || statusUpdatingId) return;

    setStatusUpdatingId(userId);
    setStatusUpdateMessage(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userId}/group/${Number(groupId)}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: nextStatus })
        }
      );
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP ${response.status}`);
      }

      setStatusUpdateMessage({
        type: 'success',
        text: `${player.name} เปลี่ยนเป็น ${nextStatus} แล้ว`
      });

      if (onCreatedRef.current) {
        await onCreatedRef.current(responseData);
      }
    } catch (statusError) {
      setStatusUpdateMessage({
        type: 'error',
        text: statusError.message || 'อัปเดตสถานะไม่สำเร็จ'
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Pick One ใช้ผู้เล่นคนแรกของลิสต์เป็นค่าเริ่มต้นเสมอ เมื่อคนเดิม
  // เปลี่ยนเป็น QUEUE/PLAY หรือไม่อยู่ในเงื่อนไขที่เลือกได้ ระบบจะเลื่อนไปคนถัดไป
  useEffect(() => {
    if (mode !== 'PICK_ONE') return;

    const currentId = Number(pickedPlayer?.user_id);
    const currentPlayer = pickOneSelectablePlayers.find(
      player => Number(player.user_id) === currentId
    );

    if (currentPlayer) {
      const selectableIds = new Set(
        pickOneSelectablePlayers.map(player => Number(player.user_id))
      );
      const validAdditionalPlayers = pickOnePlayers.filter(
        player => selectableIds.has(Number(player.user_id))
      );

      if (validAdditionalPlayers.length !== pickOnePlayers.length) {
        setPickOnePlayers(validAdditionalPlayers);
        setError('');
        setAiRecommendation(null);
        aiProcessedSignatureRef.current = '';
      }
      return;
    }

    const nextPlayer = pickOneSelectablePlayers[0] || null;
    setPickedPlayer(nextPlayer);
    setPickOnePlayers([]);
    setError('');
    setAiRecommendation(null);
    setAiAutomationStatus(
      nextPlayer
        ? `เลือก ${nextPlayer.name} เป็นผู้เล่นหลักอัตโนมัติแล้ว`
        : 'กำลังรอผู้เล่น ACTIVE อย่างน้อย 4 คน'
    );
    aiProcessedSignatureRef.current = '';
    pickOneAttemptedIdsRef.current.clear();
  }, [mode, pickOneSelectablePlayers, pickedPlayer, pickOnePlayers]);

  const priorityPlayers = useMemo(() => {
    const search = priorityKeyword.trim().toLowerCase();
    if (!search) return orderedSelectablePlayers;

    return orderedSelectablePlayers.filter(player => [
      player.name,
      player.point,
      player.level_rank,
      getPlayStyleLabel(player.daily_play_style),
      getPlayerGames(player)
    ]
      .filter(value => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase()
      .includes(search));
  }, [orderedSelectablePlayers, priorityKeyword]);

  const selectedManualPlayers = manualPlayers.filter(Boolean);
  const selectedPriorityIdSet = new Set(
    selectedManualPlayers.map(player => Number(player.user_id))
  );
  const orderedPrioritySelections = orderedSelectablePlayers.filter(
    player => selectedPriorityIdSet.has(Number(player.user_id))
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setApprovePlaying(false);
    setPriority('WAIT_LONGEST');
    setPointRange(100);
    setPlayStyle('ANY');
    setMatchCount(1);
    setPriorityKeyword('');
    setPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
    setLastResult(null);
    setError('');
    setAiAutoEnabled(false);
    setAiAutoLoading(false);
    setAiAutomationStatus('');
    setAiRecommendation(null);
    setAdvancedSettingsOpen(false);
    setExpandedInfoPanel(null);
    aiProcessedSignatureRef.current = '';
    pickOneAttemptedIdsRef.current.clear();
  }, [groupId]);

  useEffect(() => {
    if (!supportsMultipleMatches) {
      setMatchCount(1);
      return;
    }

    setMatchCount(current => Math.min(current, maxMatchCount));
  }, [maxMatchCount, supportsMultipleMatches]);

  const resetInputs = () => {
    setPriorityKeyword('');
    setPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
  };

  const togglePriorityPlayer = player => {
    const playerId = Number(player.user_id);
    const lockedPartner = getLockedPartner(player);
    const partnerStatus = getStatus(lockedPartner);

    if (lockedPartner && partnerStatus !== 'ACTIVE') {
      setError(
        `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus || 'ไม่พร้อม'} ` +
        'จึงยังเลือกคู่นี้ไม่ได้'
      );
      return;
    }

    const selectionUnit = [player, lockedPartner]
      .filter(Boolean)
      .filter((item, index, items) =>
        items.findIndex(
          candidate =>
            Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const selectionIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = selectedManualPlayers.some(
      item => Number(item.user_id) === playerId
    );
    const addingPlayers = selectionUnit.filter(
      item => !selectedManualPlayers.some(
        selected =>
          Number(selected.user_id) === Number(item.user_id)
      )
    );

    if (
      !alreadySelected &&
      selectedManualPlayers.length + addingPlayers.length > 4
    ) {
      setError(
        lockedPartner
          ? `ต้องเลือก ${player.name} พร้อมคู่ล็อก ${lockedPartner.name} ` +
            'แต่จำนวนผู้เล่นจะเกิน 4 คน'
          : 'เลือกผู้เล่นได้สูงสุด 4 คน กรุณาเอาคนเดิมออกก่อน'
      );
      return;
    }

    setError('');
    setManualPlayers(currentPlayers => {
      const currentSelected = currentPlayers.filter(Boolean);

      if (alreadySelected) {
        const remaining = currentSelected.filter(
          item => !selectionIds.has(Number(item.user_id))
        );
        return [...remaining, ...Array(4 - remaining.length).fill(null)];
      }

      const nextSelected = [...currentSelected, ...addingPlayers];
      return [...nextSelected, ...Array(4 - nextSelected.length).fill(null)];
    });
  };

  const handleApproveChange = enabled => {
    setApprovePlaying(enabled);
    setError('');

    if (!enabled) {
      if (pickedPlayer && getStatus(pickedPlayer) !== 'ACTIVE') {
        setPickedPlayer(null);
        setPickOnePlayers([]);
      }

      setManualPlayers(currentPlayers =>
        currentPlayers.map(player =>
          player && getStatus(player) !== 'ACTIVE' ? null : player
        )
      );
    }
  };

  const getLockedPartner = useCallback(player => {
    if (!player) return null;

    const playerId = Number(player.user_id);
    const lock = lockedPairs.find(item =>
      Number(item.user_a) === playerId ||
      Number(item.user_b) === playerId
    );

    if (!lock) return null;

    const partnerId =
      Number(lock.user_a) === playerId
        ? Number(lock.user_b)
        : Number(lock.user_a);

    return normalizedPlayers.find(
      item => Number(item.user_id) === partnerId
    ) || null;
  }, [lockedPairs, normalizedPlayers]);

  const pickedLockedPartner = getLockedPartner(pickedPlayer);
  const pickOneOrderedPlayers = useMemo(() => {
    const ordered = [pickedPlayer, pickedLockedPartner, ...pickOnePlayers]
      .filter(Boolean);

    return ordered.filter((player, index, players) =>
      players.findIndex(
        item => Number(item.user_id) === Number(player.user_id)
      ) === index
    );
  }, [pickedLockedPartner, pickedPlayer, pickOnePlayers]);

  const pickOneNearbyCandidates = useMemo(() => {
    if (!pickedPlayer) return [];

    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(pickedLockedPartner?.user_id)
    ].filter(Number.isFinite));

    const keyword = nearbySearchKeyword.trim().toLowerCase();
    return normalizedPlayers
      .filter(player => !fixedIds.has(Number(player.user_id)))
      .filter(player =>
        !keyword || String(player.name || '').toLowerCase().includes(keyword)
      )
      .sort((left, right) => {
        const statusDiff =
          getPickOneStatusPriority(left) - getPickOneStatusPriority(right);
        if (statusDiff !== 0) return statusDiff;

        const gameDiff = getPlayerGames(left) - getPlayerGames(right);
        if (gameDiff !== 0) {
          return nearbyGameSort === 'DESC' ? -gameDiff : gameDiff;
        }

        return (
          getRankDistance(pickedPlayer, left) -
            getRankDistance(pickedPlayer, right) ||
          Math.abs(Number(left.point || 0) - Number(pickedPlayer.point || 0)) -
            Math.abs(Number(right.point || 0) - Number(pickedPlayer.point || 0)) ||
          comparePlayersByPriority(left, right, priority)
        );
      });
  }, [
    nearbyGameSort,
    nearbySearchKeyword,
    normalizedPlayers,
    pickedLockedPartner,
    pickedPlayer,
    priority
  ]);

  const pickOneNearbyPlayers = pickOneNearbyCandidates;

  useEffect(() => {
    setNearbySearchKeyword('');
  }, [pickedPlayer?.user_id]);

  const togglePickOneNearbyPlayer = player => {
    if (!pickedPlayer) return;

    const playerId = Number(player.user_id);
    const candidatePartner = getLockedPartner(player);
    const anchorPartner = getLockedPartner(pickedPlayer);
    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(anchorPartner?.user_id)
    ].filter(Number.isFinite));
    const selectionUnit = [player, candidatePartner]
      .filter(Boolean)
      .filter(item => !fixedIds.has(Number(item.user_id)))
      .filter((item, index, items) =>
        items.findIndex(
          candidate => Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const unitIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = pickOnePlayers.some(
      item => Number(item.user_id) === playerId
    );

    if (alreadySelected) {
      setPickOnePlayers(current => current.filter(
        item => !unitIds.has(Number(item.user_id))
      ));
      setError('');
      return;
    }

    const unavailablePlayer = selectionUnit.find(
      item => getStatus(item) !== 'ACTIVE'
    );
    if (unavailablePlayer && !approvePlaying) {
      setError(
        `${unavailablePlayer.name} อยู่สถานะ ${getStatus(unavailablePlayer)} ` +
        'กรุณาเปิด Approve สถานะอื่นเพื่อจัด Match ล่วงหน้า'
      );
      return;
    }

    const currentIds = new Set(
      pickOnePlayers.map(item => Number(item.user_id))
    );
    const addingPlayers = selectionUnit.filter(
      item => !currentIds.has(Number(item.user_id))
    );
    const maximumAdditionalPlayers = 4 - fixedIds.size;

    if (pickOnePlayers.length + addingPlayers.length > maximumAdditionalPlayers) {
      setError(
        `Pick One เลือกเพิ่มได้อีกไม่เกิน ${maximumAdditionalPlayers} คน ` +
        '(รวม Locked Pair ต้องมีผู้เล่นไม่เกิน 4 คน)'
      );
      return;
    }

    setPickOnePlayers(current => [...current, ...addingPlayers]);
    setError('');
  };

  const selectedNonActivePlayers = () => {
    if (mode === 'PICK_ONE') {
      const lockedPartner = getLockedPartner(pickedPlayer);
      return [pickedPlayer, lockedPartner, ...pickOnePlayers]
        .filter(Boolean)
        .filter(player => getStatus(player) !== 'ACTIVE')
        .filter((player, index, players) =>
          players.findIndex(
            item => Number(item.user_id) === Number(player.user_id)
          ) === index
        );
    }

    if (usesManualSelection) {
      return manualPlayers.filter(
        player => player && getStatus(player) !== 'ACTIVE'
      );
    }

    return [];
  };

  const validate = () => {
    if (!groupId) return 'ไม่พบ Group';

    if (
      AUTOMATIC_MODES.has(mode) &&
      activePlayers.length < requiredPlayerCount
    ) {
      return (
        `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
        `แต่การสร้าง ${matchCount} Match ต้องใช้ ${requiredPlayerCount} คน`
      );
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      return 'กรุณาเลือกผู้เล่นที่ต้องการล็อก';
    }

    if (mode === 'PICK_ONE') {
      const pickedId = Number(pickedPlayer.user_id);
      const lockedPartner = getLockedPartner(pickedPlayer);
      const pickedLock = lockedPairs.find(item =>
        Number(item.user_a) === pickedId ||
        Number(item.user_b) === pickedId
      );

      if (pickedLock && !lockedPartner) {
        return (
          `ไม่พบคู่ล็อกของ ${pickedPlayer.name} ในรายชื่อผู้เล่นที่ใช้งานได้`
        );
      }

      if (lockedPartner) {
        const partnerStatus = getStatus(lockedPartner);

        if (partnerStatus !== 'ACTIVE' && !approvePlaying) {
          return (
            `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus} ` +
            'กรุณาเปิด Approve สถานะอื่น'
          );
        }

      }

      const approvedPickIds = new Set(
        pickOneOrderedPlayers.map(player => Number(player.user_id))
      );
      const allowedPlayers = normalizedPlayers.filter(player => {
        const status = getStatus(player);
        return (
          status === 'ACTIVE' ||
          (
            approvePlaying &&
            approvedPickIds.has(Number(player.user_id))
          )
        );
      });

      if (allowedPlayers.length < 4) {
        return (
          `ผู้เล่นที่อยู่ในสถานะพร้อมมีเพียง ${allowedPlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คน'
        );
      }

      // approvedPickIds มีผู้เล่นหลัก, Locked Pair และผู้เล่นที่เลือกเพิ่ม
      // ครบอยู่แล้ว ห้ามบวก pickOnePlayers ซ้ำอีกครั้ง
      if (approvedPickIds.size > 4) {
        return 'Pick One มีผู้เล่นที่เลือกและ Locked Pair รวมเกิน 4 คน';
      }
    }

    if (mode === 'PRIORITY_SELECT') {
      if (activePlayers.length < 4) {
        return (
          `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คนจึงจะ Random ได้'
        );
      }

      if (selectedManualPlayers.length < 1) {
        return 'กรุณาเลือกผู้เล่นอย่างน้อย 1 คนก่อนกด Random';
      }
    }

    if (mode === 'MANUAL_4') {
      if (manualPlayers.some(player => !player)) {
        return 'กรุณาเลือกผู้เล่นให้ครบ 4 คน';
      }

      const ids = manualPlayers.map(player => Number(player.user_id));
      if (new Set(ids).size !== 4) return 'พบผู้เล่นซ้ำ';
    }

    const nonActiveSelections = selectedNonActivePlayers();
    if (nonActiveSelections.length > 0 && !approvePlaying) {
      return 'กรุณาเปิด Approve สถานะอื่นก่อนเลือกผู้เล่นที่ไม่ใช่ ACTIVE';
    }

    return '';
  };

  const generateQueue = async () => {
    if (loading || aiAutoLoading) return;

    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: supportsMultipleMatches ? matchCount : 1,
        approvePlaying: canApprovePlaying && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : usesManualSelection
            ? (
              mode === 'PRIORITY_SELECT'
                ? orderedPrioritySelections
                : selectedManualPlayers
            ).map(
                player => Number(player.user_id)
              )
            : []
      };

      const requestQueue = async payload => {
        const response = await fetch(
          `${API_BASE}/api/game-match/generate-queue`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );
        const responseData = await response.json().catch(() => ({}));

        return { response, responseData };
      };

      let requestBody = {
        ...body,
        allowRangeOverride: false,
        allowRepeatPartnerOverride: false,
        allowRecentEncounterOverride: false
      };
      let res;
      let data;

      // อาจต้องยืนยันมากกว่า 1 ชั้น เช่น คะแนนเกินช่วง แล้วจึงพบคู่ทีมซ้ำ
      for (let confirmationRound = 0; confirmationRound < 3; confirmationRound += 1) {
        ({ response: res, responseData: data } = await requestQueue(
          requestBody
        ));

        if (res.ok) break;

        if (
          data.confirmationType === 'RANGE' &&
          usesIndividualPointRange &&
          !requestBody.allowRangeOverride
        ) {
          const gaps = Array.isArray(data.individualGaps)
            ? data.individualGaps.join(' / ')
            : '-';
          const teammateGaps = Array.isArray(data.teammateGaps)
            ? data.teammateGaps.join(' / ')
            : '-';
          const requiredRange = data.requiredRange ?? gaps;
          const confirmed = window.confirm(
            `⚠️ ไม่พบทีมที่ผ่านช่วง ${pointRange} คะแนน\n` +
            `คะแนนห่างข้ามทีม: ${gaps}\n` +
            `คะแนนห่างภายในทีม T1 / T2: ${teammateGaps}\n` +
            `ต้องใช้ช่วงสูงสุด ${requiredRange} คะแนน\n` +
            'ระบบยังคงรักษา Locked Pair ให้อยู่ทีมเดียวกัน\n\n' +
            'ต้องการ Approve คะแนนห่างและสร้าง Match ด้วยคู่นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยกเลิกการสร้าง Match เพราะคะแนนเกินช่วงที่กำหนด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRangeOverride: true
          };
          continue;
        }

        if (
          (
            data.confirmationType === 'RECENT_ENCOUNTER' ||
            data.confirmationType === 'REPEAT_PARTNER'
          ) &&
          !requestBody.allowRecentEncounterOverride
        ) {
          const repeatPairs = Array.isArray(data.recentEncounterPairs)
            ? data.recentEncounterPairs
            : Array.isArray(data.repeatPartnerPairs)
              ? data.repeatPartnerPairs
            : [];
          const pairNames = repeatPairs.length > 0
            ? repeatPairs
              .map(pair => {
                const relation = pair.relation === 'TEAMMATE'
                  ? 'ทีมเดียวกัน'
                  : pair.relation === 'OPPONENT'
                    ? 'คนละฝั่ง'
                    : 'อยู่ในแมทช์เดียวกัน';
                return `${pair.nameA} ↔ ${pair.nameB} (${relation})`;
              })
              .join('\n')
            : 'ผู้เล่นที่เพิ่งพบกัน';
          const expansionNote =
            mode === 'PICK_ONE' || mode === 'AI_RANDOM'
              ? 'ระบบลองขยับช่วงคะแนนและ Rank เพื่อหาชุดที่ใกล้ที่สุดแล้ว แต่'
              : '';
          const confirmed = window.confirm(
            `⚠️ ไม่มีชุดผู้เล่นที่ผ่านกติกา 2 เกมล่าสุด\n${pairNames}\n\n` +
            `${expansionNote}รายชื่อข้างต้นยังเคยอยู่ใน Match เดียวกันภายใน 2 เกมล่าสุด ` +
            'ไม่ว่าจะเป็นทีมเดียวกันหรือคนละฝั่ง\n\n' +
            'ต้องการ Approve และสร้าง Match นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยังไม่ได้สร้าง Match เพราะยังไม่ได้ Approve ผู้เล่นซ้ำใน 2 เกมล่าสุด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRepeatPartnerOverride: true,
            allowRecentEncounterOverride: true
          };
          continue;
        }

        break;
      }

      if (!res.ok) {
        throw new Error(
          data.message ||
          data.error ||
          `สร้าง Queue ไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      setLastResult(data);
      resetInputs();

      if (onCreatedRef.current) {
        await onCreatedRef.current(data);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const supportsAiAuto =
      mode === 'AI_RANDOM' || mode === 'PICK_ONE';

    if (!supportsAiAuto) {
      setAiRecommendation(null);
      setAiAutomationStatus('');
      setAiAutoLoading(false);
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      setAiRecommendation(null);
      setAiAutomationStatus('เลือกผู้เล่นหลักก่อน เพื่อให้ AI ค้นหาคะแนนใกล้เคียง');
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    const approvedAdvancePlayerCount =
      mode === 'PICK_ONE' && approvePlaying
        ? pickOneOrderedPlayers.filter(
          player => getStatus(player) !== 'ACTIVE'
        ).length
        : 0;
    const availablePlayerCount =
      aiOrderedActivePlayers.length + approvedAdvancePlayerCount;
    const missingPlayerCount = Math.max(
      0,
      requiredPlayerCount - availablePlayerCount
    );

    if (!groupId || missingPlayerCount > 0) {
      setAiAutomationStatus(
        `กำลังรอผู้เล่น ACTIVE อีก ${missingPlayerCount} คน`
      );
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (
      aiRequestInFlightRef.current ||
      aiProcessedSignatureRef.current === aiMatchmakingSignature
    ) {
      return undefined;
    }

    let cancelled = false;
    const timerId = window.setTimeout(async () => {
      aiRequestInFlightRef.current = true;
      setAiAutoLoading(true);
      setError('');
      setAiAutomationStatus(
        mode === 'PICK_ONE'
          ? `AI กำลังค้นหาผู้เล่นคะแนนใกล้เคียง ${pickedPlayer.name}...`
          : 'AI กำลังวิเคราะห์ผู้เล่น ACTIVE ทั้งก๊วน...'
      );

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: mode === 'AI_RANDOM' ? matchCount : 1,
        approvePlaying: mode === 'PICK_ONE' && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : [],
        // Pick One Auto ต้องจัดลงให้ได้ โดยยังเลือกชุดที่คะแนนใกล้ที่สุด
        // จากผู้เล่นที่มีอยู่ หากไม่มีชุดที่อยู่ในช่วงคะแนนที่ตั้งไว้
        allowRangeOverride: mode === 'PICK_ONE',
        allowRepeatPartnerOverride: false,
        allowRecentEncounterOverride: false
      };

      try {
        const preview = await postMatchmakingJson(
          '/api/game-match/random',
          body
        );

        if (cancelled) return;

        setAiRecommendation(preview);
        aiProcessedSignatureRef.current = aiMatchmakingSignature;
        setAiAutomationStatus(
          aiAutoEnabled
            ? mode === 'PICK_ONE'
              ? 'พบผู้เล่นคะแนนใกล้เคียงแล้ว กำลังสร้าง Queue อัตโนมัติ...'
              : `พบ Match แนะนำแล้ว กำลังสร้าง Queue ${matchCount} Match อัตโนมัติ...`
            : 'อัปเดต Match แนะนำแบบเรียลไทม์แล้ว'
        );

        if (!aiAutoEnabled) return;

        await new Promise(resolve => {
          window.setTimeout(resolve, 700);
        });

        if (cancelled) return;

        const created = await postMatchmakingJson(
          '/api/game-match/generate-queue',
          body
        );

        if (cancelled) return;

        const createdRecommendation =
          Array.isArray(created.matches) && created.matches.length > 0
            ? created.matches[0]
            : created;

        setAiRecommendation(createdRecommendation);
        setLastResult(created);
        setAiAutomationStatus(
          `สร้าง Queue ${created.matchCount || (mode === 'PICK_ONE' ? 1 : matchCount)} Match อัตโนมัติสำเร็จ`
        );
        if (mode === 'PICK_ONE') {
          pickOneAttemptedIdsRef.current.clear();
        }

        if (onCreatedRef.current) {
          await onCreatedRef.current(created);
        }
      } catch (requestError) {
        if (!cancelled) {
          if (mode === 'PICK_ONE' && aiAutoEnabled) {
            const currentId = Number(pickedPlayer?.user_id);
            pickOneAttemptedIdsRef.current.add(currentId);

            const nextPlayer = orderedSelectablePlayers.find(player => {
              const playerId = Number(player.user_id);
              return (
                playerId !== currentId &&
                !pickOneAttemptedIdsRef.current.has(playerId)
              );
            });

            if (nextPlayer) {
              setPickedPlayer(nextPlayer);
              setPickOnePlayers([]);
              setAiRecommendation(null);
              setError('');
              setAiAutomationStatus(
                `${pickedPlayer.name} จัดไม่ได้ • กำลังลองผู้เล่นถัดไป ${nextPlayer.name}`
              );
              aiProcessedSignatureRef.current = '';
              return;
            }
          }

          aiProcessedSignatureRef.current = aiMatchmakingSignature;
          setError(requestError.message);
          if (
            requestError.data?.requiresRecentEncounterApproval ||
            requestError.data?.requiresRepeatPartnerApproval
          ) {
            setAiAutoEnabled(false);
          }
          setAiAutomationStatus(
            requestError.data?.requiresRecentEncounterApproval ||
              requestError.data?.requiresRepeatPartnerApproval
              ? 'พบผู้เล่นที่อยู่ใน 2 เกมล่าสุด กรุณากดสร้าง Queue เพื่อ Approve'
              : 'AI ยังสร้าง Queue ไม่สำเร็จ'
          );
        }
      } finally {
        aiRequestInFlightRef.current = false;
        setAiAutoLoading(false);
        setAiAutomationCycle(current => current + 1);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    aiAutoEnabled,
    aiAutomationCycle,
    aiMatchmakingSignature,
    aiOrderedActivePlayers.length,
    approvePlaying,
    groupId,
    matchCount,
    mode,
    orderedSelectablePlayers,
    pickOnePlayers,
    pickOneOrderedPlayers,
    pickedPlayer,
    playStyle,
    pointRange,
    priority,
    requiredPlayerCount
  ]);

  const renderTeam = (title, players) => (
    <div className="rmp-result-team">
      <strong>{title}</strong>
      {players.map(player => (
        <div key={player.user_id} className="rmp-result-player">
          <span>
            {player.name}
            {getStatus(player) === 'PLAY' && (
              <small className="rmp-inline-play">PLAY</small>
            )}
          </span>
          <span>{player.point}</span>
        </div>
      ))}
    </div>
  );

  const createdMatches = lastResult
    ? Array.isArray(lastResult.matches) && lastResult.matches.length > 0
      ? lastResult.matches
      : [lastResult]
    : [];

  const getGenerateButtonLabel = () => {
    if (loading || aiAutoLoading) {
      if (mode === 'AI_RANDOM') {
        return '🤖 AI กำลังวิเคราะห์และสร้าง Queue...';
      }

      return mode === 'PRIORITY_SELECT' &&
        selectedManualPlayers.length < 4
        ? 'กำลังสุ่มผู้เล่นที่เหลือ...'
        : `กำลังสร้าง ${matchCount} Match...`;
    }

    if (mode === 'PRIORITY_SELECT') {
      if (selectedManualPlayers.length === 0) {
        return '🎲 เลือกอย่างน้อย 1 คนเพื่อ Random';
      }

      if (selectedManualPlayers.length < 4) {
        return (
          `🎲 Random อีก ${4 - selectedManualPlayers.length} คน ` +
          'และสร้าง Queue'
        );
      }

      return '➕ สร้าง Queue จากผู้เล่น 4 คน';
    }

    if (mode === 'AI_RANDOM') {
      return aiAutoEnabled
        ? `🤖 AI รอสร้าง Queue อัตโนมัติครั้งละ ${matchCount} Match`
        : `🤖 สร้าง Queue ${matchCount} Match จาก AI`;
    }

    if (mode === 'PICK_ONE' && aiAutoEnabled) {
      return '🤖 Pick One AI Auto กำลังรอสร้าง Queue';
    }

    if (supportsMultipleMatches && matchCount > 1) {
      return `➕ สร้าง Queue ${matchCount} Match`;
    }

    return '➕ สร้าง Queue';
  };

  if (isMonitorAi) {
    const missingPlayers = Math.max(
      0,
      requiredPlayerCount - aiOrderedActivePlayers.length
    );

    return (
      <aside
        className={`rmp-monitor-ai ${aiAutoEnabled ? 'is-running' : 'is-stopped'}`}
        aria-label="AI Random อัตโนมัติ"
      >
        <div className="rmp-monitor-ai-title">
          <span className={`rmp-monitor-ai-dot ${aiAutoLoading ? 'is-loading' : ''}`} />
          <span>
            <strong>🤖 AI Random</strong>
            <small>
              ACTIVE {aiOrderedActivePlayers.length} คน
              {missingPlayers > 0 ? ` • รออีก ${missingPlayers}` : ' • พร้อมจัด'}
            </small>
          </span>
        </div>

        <div className="rmp-monitor-ai-count" aria-label="จำนวน Match ต่อรอบ">
          {[1, 3].map(count => (
            <button
              type="button"
              key={count}
              className={matchCount === count ? 'selected' : ''}
              disabled={aiAutoEnabled || aiAutoLoading}
              onClick={() => {
                setMatchCount(count);
                setError('');
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
              }}
            >
              {count} Match
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`rmp-monitor-ai-toggle ${aiAutoEnabled ? 'stop' : 'start'}`}
          onClick={() => {
            const nextEnabled = !aiAutoEnabled;
            setAiAutoEnabled(nextEnabled);
            setError('');
            setAiAutomationStatus(
              nextEnabled
                ? `เริ่ม AI สร้างครั้งละ ${matchCount} Match`
                : 'หยุด AI Random แล้ว'
            );
            aiProcessedSignatureRef.current = '';
          }}
        >
          {aiAutoEnabled ? '■ หยุด' : '▶ เริ่ม'}
        </button>

        <span className="rmp-monitor-ai-status" title={aiAutomationStatus || undefined}>
          {aiAutoLoading
            ? 'กำลังวิเคราะห์...'
            : aiAutoEnabled
              ? aiAutomationStatus || 'กำลังทำงาน'
              : 'หยุดอยู่'}
        </span>

        {error && <span className="rmp-monitor-ai-error">⚠ {error}</span>}
      </aside>
    );
  }

  return (
    <section
      className="section-card random-match-panel"
      data-feature-version="daily-play-style-v3"
    >
      <div className="rmp-heading">
        <div>
          <h3>🎲 Matchmaking</h3>
          <p>เลือกวิธีจัดผู้เล่น ระบบจะสร้าง Queue ใน transaction เดียว</p>
        </div>
        <div className="rmp-player-counts">
          <span className="rmp-active-count">
            ACTIVE {activePlayers.length} คน
          </span>
          {playingPlayers.length > 0 && (
            <span className="rmp-playing-count">
              PLAY {playingPlayers.length} คน
            </span>
          )}
        </div>
      </div>

      <div className="rmp-mode-grid">
        {MODES.map(item => (
          <label
            key={item.value}
            className={`rmp-mode-card ${mode === item.value ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="matchmaking-mode"
              value={item.value}
              checked={mode === item.value}
              onChange={() => {
                setMode(item.value);
                setApprovePlaying(false);
                setAiAutoEnabled(false);
                setAiAutomationStatus('');
                setAiRecommendation(null);
                setAdvancedSettingsOpen(false);
                setExpandedInfoPanel(null);
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
                if (item.value === 'PRIORITY_SELECT') {
                  setPriority('WAIT_LONGEST');
                }
                if (item.value === 'AI_RANDOM') {
                  setPriority('FIFO');
                  setPlayStyle('ANY');
                  setMatchCount(1);
                }
                if (!AUTOMATIC_MODES.has(item.value)) setMatchCount(1);
                resetInputs();
                setLastResult(null);
                setError('');
              }}
            />
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
          </label>
        ))}
      </div>

      {(mode === 'AI_RANDOM' || mode === 'PICK_ONE') && (
        <div className="rmp-ai-auto-panel">
          <div className="rmp-ai-auto-heading">
            <div>
              <strong>
                {mode === 'PICK_ONE'
                  ? '🤖 Pick One AI Auto'
                  : '🤖 AI Match แนะนำแบบเรียลไทม์'}
              </strong>
              <small>
                {mode === 'PICK_ONE'
                  ? 'เลือกผู้เล่นหลัก 1 คน แล้ว AI จะค้นหาอีก 3 คนที่คะแนนใกล้เคียงที่สุด พร้อมจัดผลรวมทีมให้สูสี'
                  : 'ยึดผู้เล่น ACTIVE ที่มาถึงก่อน แล้วค้นหาจากผู้เล่นทั้งก๊วน โดยให้คะแนนรายบุคคลใกล้กันก่อน แล้วจึงให้ผลรวมทีมสูสี (คู่ล็อกคะแนนต่างกันจะยึดผลรวมทีมก่อน)'}
              </small>
            </div>

            <label className="rmp-ai-auto-switch">
              <input
                type="checkbox"
                checked={aiAutoEnabled}
                disabled={mode === 'PICK_ONE' && !pickedPlayer}
                onChange={event => {
                  setAiAutoEnabled(event.target.checked);
                  setAiAutomationStatus('');
                  setError('');
                  aiProcessedSignatureRef.current = '';
                  pickOneAttemptedIdsRef.current.clear();
                }}
              />
              <span>
                {mode === 'PICK_ONE'
                  ? 'สร้าง Queue เมื่อพบคะแนนใกล้เคียง'
                  : 'สร้าง Queue อัตโนมัติ (ค่าเริ่มต้นปิด)'}
              </span>
            </label>
          </div>

          <div
            className="rmp-ai-live-status"
            role="status"
            aria-live="polite"
          >
            <span className={aiAutoLoading ? 'is-live' : ''} />
            {aiAutomationStatus ||
              'ระบบพร้อมตรวจผู้เล่น ACTIVE แบบเรียลไทม์'}
          </div>

          {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]) && (
            <div className="rmp-ai-anchor">
              <span>
                {mode === 'PICK_ONE' ? 'ผู้เล่นหลัก Pick One' : 'มาก่อน ลำดับ #1'}
              </span>
              <strong>
                {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).name}
              </strong>
              <span>
                {Number((mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).point || 0)} คะแนน
              </span>
              {mode === 'AI_RANDOM' && (
                <span>
                  รอ {formatWaitTime(aiOrderedActivePlayers[0], now)}
                </span>
              )}
            </div>
          )}

          {aiRecommendation && (
            <article
              className={`rmp-ai-recommendation ${
                recommendationVisible ? '' : 'is-collapsed'
              }`}
            >
              <div className="rmp-ai-recommendation-heading">
                <div>
                  <strong>Match แนะนำล่าสุด</strong>
                  <small>
                    ค้นหาจาก ACTIVE {aiRecommendation.aiPoolSize ||
                      aiOrderedActivePlayers.length} คน
                  </small>
                </div>
                <div className="rmp-ai-recommendation-actions">
                  <span>
                    Team gap {aiRecommendation.analysis?.teamGap ?? '-'}
                  </span>
                  <button
                    type="button"
                    className="rmp-ai-recommendation-toggle"
                    aria-expanded={recommendationVisible}
                    onClick={() => setRecommendationVisible(current => !current)}
                  >
                    {recommendationVisible ? '▴ ซ่อน Match แนะนำ' : '▾ แสดง Match แนะนำ'}
                  </button>
                </div>
              </div>

              {recommendationVisible && (
                <>
                  <div className="rmp-result-teams">
                    {renderTeam(
                      'Team 1',
                      aiRecommendation.team1 || []
                    )}
                    <span className="rmp-vs">VS</span>
                    {renderTeam(
                      'Team 2',
                      aiRecommendation.team2 || []
                    )}
                  </div>

                  <p className="rmp-reason">
                    {aiRecommendation.reason}
                  </p>
                </>
              )}
            </article>
          )}
        </div>
      )}

      <button
        type="button"
        className={`rmp-advanced-settings-toggle ${
          advancedSettingsOpen ? 'is-open' : ''
        }`}
        aria-expanded={advancedSettingsOpen}
        onClick={() => {
          setAdvancedSettingsOpen(current => !current);
          if (advancedSettingsOpen) setExpandedInfoPanel(null);
        }}
      >
        <span>⚙️ ตั้งค่าเพิ่มเติม</span>
        <small>
          {priority === 'FIFO' ? 'มาก่อน' : priority === 'LEAST_GAMES' ? 'เล่นน้อย' : 'รอนาน'}
          {' • '}ช่วงคะแนน {pointRange}
        </small>
        <b>{advancedSettingsOpen ? '▲ ซ่อน' : '▼ เปิด'}</b>
      </button>

      {advancedSettingsOpen && (
        <div className="rmp-advanced-settings-panel">
      {mode !== 'MANUAL_4' && mode !== 'AI_RANDOM' && (
        <div className="rmp-field">
          <label htmlFor="matchmaking-priority">
            {mode === 'PRIORITY_SELECT'
              ? 'เรียงรายชื่อผู้เล่น'
              : 'ลำดับความสำคัญ'}
          </label>
          <select
            id="matchmaking-priority"
            value={priority}
            onChange={event => {
              setPriority(event.target.value);
              setError('');
            }}
          >
            {mode !== 'PRIORITY_SELECT' && (
              <option value="FIFO">มาก่อน ได้เล่นก่อน (FIFO)</option>
            )}
            <option value="WAIT_LONGEST">
              รอนานที่สุด → มาก่อน
            </option>
            <option value="LEAST_GAMES">
              เล่นน้อยที่สุด → มาก่อน
            </option>
          </select>
        </div>
      )}

      <div
        className="rmp-detail-toggle-bar"
        role="group"
        aria-label="เปิดดูรายละเอียดเงื่อนไขจัด Match"
      >
        <button
          type="button"
          className={`rmp-detail-icon-button ${
            expandedInfoPanel === 'mood' ? 'is-open' : ''
          }`}
          aria-expanded={expandedInfoPanel === 'mood'}
          aria-controls="rmp-mood-detail"
          title={`Mood: ${getPlayStyleLabel(playStyle)} • กดเพื่อดูหรือเปลี่ยน`}
          onClick={() => setExpandedInfoPanel(current => (
            current === 'mood' ? null : 'mood'
          ))}
        >
          <span className="rmp-detail-icon" aria-hidden="true">🎭</span>
          <small>Mood</small>
        </button>

        {mode !== 'MANUAL_4' && (
          <>
            <button
              type="button"
              className={`rmp-detail-icon-button ${
                expandedInfoPanel === 'rank' ? 'is-open' : ''
              }`}
              aria-expanded={expandedInfoPanel === 'rank'}
              aria-controls="rmp-rank-detail"
              title="กดดูขั้นการขยาย Rank และช่วงคะแนน"
              onClick={() => setExpandedInfoPanel(current => (
                current === 'rank' ? null : 'rank'
              ))}
            >
              <span className="rmp-detail-icon" aria-hidden="true">🎯</span>
              <small>Rank</small>
            </button>

            <button
              type="button"
              className={`rmp-detail-icon-button ${
                expandedInfoPanel === 'repeat' ? 'is-open' : ''
              }`}
              aria-expanded={expandedInfoPanel === 'repeat'}
              aria-controls="rmp-repeat-detail"
              title="กดดูเงื่อนไขป้องกันคู่ซ้ำ"
              onClick={() => setExpandedInfoPanel(current => (
                current === 'repeat' ? null : 'repeat'
              ))}
            >
              <span className="rmp-detail-icon" aria-hidden="true">🔁</span>
              <small>คู่ซ้ำ</small>
            </button>
          </>
        )}
      </div>

      {/* แสดงตัวเลือก Mood เมื่อกดไอคอน โดยค่าที่เลือกจะไม่หายตอนย่อ */}
      <div
        id="rmp-mood-detail"
        className="rmp-play-style-panel rmp-collapsible-detail"
        data-testid="daily-play-style-selector"
        hidden={expandedInfoPanel !== 'mood'}
      >
        <div className="rmp-option-heading">
          <strong>Mood ของวันนี้ที่ต้องการตีแบด</strong>
          <small>
            {mode === 'AI_RANDOM'
              ? 'AI RANDOM ค้นหาจาก ACTIVE ทั้งหมด แต่จะจัดผู้เล่น Mood เดียวกันให้ครบ 4 คนก่อน'
              : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
                ? 'โหมดนี้อนุญาตให้เลือกผู้เล่นต่าง Mood และอนุญาตให้มือ S/P เล่นร่วมกับ BG ได้'
              : (
                <>
                  เมื่อเลือก “ทุก Mood” ระบบจะจัดผู้เล่น Mood เดียวกันก่อน
                  และไม่ให้มือ S/P อยู่ Match เดียวกับ BG
                </>
              )}
          </small>
        </div>

        <div className="rmp-play-style-options">
          {PLAY_STYLE_OPTIONS.map(option => {
            const styleActiveCount = option.value === 'ANY'
              ? activeStyleCounts.totalActive
              : activeStyleCounts.counts[option.value] || 0;

            return (
              <button
                type="button"
                key={option.value}
                className={playStyle === option.value ? 'selected' : ''}
                aria-pressed={playStyle === option.value}
                disabled={
                  mode === 'AI_RANDOM' && option.value !== 'ANY'
                }
                onClick={() => {
                  setPlayStyle(option.value);
                  resetInputs();
                  setLastResult(null);
                  setError('');
                }}
              >
                <span>{option.icon}</span>
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                  <em className="rmp-style-active-badge">
                    ACTIVE {styleActiveCount}
                  </em>
                </span>
              </button>
            );
          })}
        </div>

        <div className="rmp-style-eligible-count">
          {mode === 'AI_RANDOM'
            ? `AI ใช้ ACTIVE ทั้งหมด ${activePlayers.length} คน • จัด Mood เดียวกันก่อน`
            : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
              ? `ข้อยกเว้น: เลือกต่าง Mood และเลือก S/P ร่วมกับ BG ได้`
              : `ผู้เล่นตรง Mood: ACTIVE ${styleMatchingActiveCount} คน`}
          {playStyle !== 'ANY' && (
            <span>
              {styleMatchingActiveCount < minimumStylePlayers
                ? (
                  <>
                    {' '}• ต้องมีอย่างน้อย {minimumStylePlayers} คน
                    จึงจะสร้าง Match ได้
                  </>
                )
                : (
                  <>
                    {' '}• แสดงเฉพาะผู้เล่นที่เลือก Mood นี้
                  </>
                )}
            </span>
          )}
          {playStyle === 'ANY' && (
            <span>
              {' '}• แสดง ACTIVE ทั้งหมด {activePlayers.length} คน
            </span>
          )}
          {activeStyleCounts.missingStyle > 0 && (
            <span className="rmp-style-missing-count">
              {' '}• ยังไม่เลือก Mood วันนี้{' '}
              {activeStyleCounts.missingStyle} คน
            </span>
          )}
        </div>
      </div>

      {mode !== 'MANUAL_4' && (
        <>
          <div
            id="rmp-rank-detail"
            className="rmp-progressive-rule rmp-collapsible-detail"
            hidden={expandedInfoPanel !== 'rank'}
          >
            <div>
              <strong>🎯 จับ Rank เดียวกันและคะแนนใกล้กันก่อน</strong>
              <small>
                หากยังไม่ครบ 4 คน ระบบจะค่อย ๆ ขยาย Rank แล้วไล่ช่วงคะแนนตามลำดับ
                โดยไม่บังคับสร้าง Match ที่คะแนนทั้งกลุ่มห่างเกิน 300
              </small>
            </div>
            <div className="rmp-progressive-steps" aria-label="ลำดับช่วงคะแนน">
              {[20, 40, 80, 100, 150, 200, 250, 300].map(step => (
                <span key={step}>≤ {step}</span>
              ))}
            </div>
          </div>

          <div
            id="rmp-repeat-detail"
            className="rmp-repeat-rule rmp-collapsible-detail"
            hidden={expandedInfoPanel !== 'repeat'}
          >
            <strong>🔁 ป้องกันคู่ซ้ำ</strong>
            <span>
              Pick One และ AI Random จะหลีกเลี่ยงทุกคนที่เพิ่งอยู่ Match เดียวกันใน
              2 เกมล่าสุดก่อน หากติดซ้ำ ระบบจะขยับคะแนนและ Rank ขึ้น/ลงทีละช่วง
              แล้วเลือกชุดที่ใกล้ที่สุด ส่วน Locked Pair อยู่ทีมเดียวกันซ้ำได้ตลอด
            </span>
          </div>
        </>
      )}

      {(supportsMultipleMatches || usesPointRange) && (
        <div className="rmp-settings-grid">
          {usesPointRange && (
            <div className="rmp-option-panel">
              <div className="rmp-option-heading">
                <strong>
                  {usesIndividualPointRange
                    ? 'เกณฑ์คะแนนข้ามทีมและภายในทีม'
                    : 'ช่วงคะแนนจากผู้เล่นคนแรก'}
                </strong>
                <small>
                  {usesIndividualPointRange
                    ? 'ทั้งคู่แข่งตำแหน่งเดียวกันข้ามทีม และผู้เล่น 2 คนภายในแต่ละทีม ต้องมีคะแนนห่างไม่เกินค่านี้'
                    : 'คนแรกเลือกตาม Priority ด้านบน ส่วนอีก 3 คนต้องมีคะแนนต่างจากคนแรกไม่เกินค่านี้'}
                </small>
              </div>

              <div className="rmp-range-options">
                {POINT_RANGES.map(range => (
                  <button
                    type="button"
                    key={range}
                    className={pointRange === range ? 'selected' : ''}
                    aria-pressed={pointRange === range}
                    onClick={() => {
                      setPointRange(range);
                      setLastResult(null);
                      setError('');
                    }}
                  >
                    ± ไม่เกิน {range}
                  </button>
                ))}
              </div>
            </div>
          )}

          {supportsMultipleMatches && (
            <div className="rmp-option-panel">
              <div className="rmp-option-heading">
                <strong>จำนวน Match ที่ต้องการสร้าง</strong>
                <small>
                  {mode === 'AI_RANDOM'
                    ? 'กำหนดจำนวน Queue ต่อหนึ่งรอบของ AI ระบบจะรอจนมี ACTIVE ครบตามจำนวน'
                    : 'ผู้เล่นจะไม่ซ้ำกันระหว่าง Match ที่สร้างพร้อมกัน'}
                </small>
              </div>

              {mode === 'AI_RANDOM' && (
                <div className="rmp-ai-count-presets">
                  {[1, 5, 10, 15].map(count => (
                    <button
                      type="button"
                      key={count}
                      className={matchCount === count ? 'selected' : ''}
                      onClick={() => {
                        setMatchCount(count);
                        setError('');
                        setLastResult(null);
                        setAiAutomationStatus('');
                        aiProcessedSignatureRef.current = '';
                      }}
                    >
                      {count} Match
                    </button>
                  ))}
                </div>
              )}

              <div className="rmp-match-count-control">
                <button
                  type="button"
                  aria-label="ลดจำนวน Match"
                  disabled={matchCount <= 1}
                  onClick={() => setMatchCount(current => Math.max(1, current - 1))}
                >
                  −
                </button>
                <select
                  aria-label="จำนวน Match"
                  value={matchCount}
                  onChange={event => setMatchCount(Number(event.target.value))}
                >
                  {Array.from({ length: maxMatchCount }, (_, index) => index + 1)
                    .map(count => (
                      <option value={count} key={count}>
                        {count} Match
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  aria-label="เพิ่มจำนวน Match"
                  disabled={matchCount >= maxMatchCount}
                  onClick={() => setMatchCount(
                    current => Math.min(maxMatchCount, current + 1)
                  )}
                >
                  ＋
                </button>
              </div>

              <div className="rmp-batch-hint">
                ใช้ผู้เล่น {requiredPlayerCount} คน • สร้างได้สูงสุด{' '}
                {availableMatchCount} Match
                {' '}จากผู้เล่น ACTIVE ปัจจุบัน
              </div>
            </div>
          )}
        </div>
      )}
        </div>
      )}

      {canApprovePlaying && (
        <label className={`rmp-approve ${approvePlaying ? 'enabled' : ''}`}>
          <input
            type="checkbox"
            checked={approvePlaying}
            onChange={event => handleApproveChange(event.target.checked)}
          />
          <span>
            <strong>Approve ทุกสถานะ</strong>
            <small>
              อนุญาตผู้เล่นทุกสถานะ เช่น PLAY, QUEUE และ CHECKOUT
              เพื่อจัด Match ล่วงหน้า รวมถึง Locked Pair แต่ไม่รวม HOLD
            </small>
          </span>
        </label>
      )}

      <section className="rmp-hold-manager">
        <div className="rmp-hold-heading">
          <div>
            <strong>⏸ พักผู้เล่นชั่วคราว (HOLD)</strong>
            <small>ค้นหาชื่อแล้วเปลี่ยน ACTIVE เป็น HOLD หรือเรียกกลับเป็น ACTIVE</small>
          </div>
          <span>HOLD {heldPlayers.length} คน</span>
        </div>

        <div className="rmp-hold-search-wrap">
          <span aria-hidden="true">🔎</span>
          <input
            type="search"
            value={statusSearchKeyword}
            placeholder="ค้นหาชื่อผู้เล่นเพื่อเปลี่ยนสถานะ HOLD / ACTIVE"
            onChange={event => {
              setStatusSearchKeyword(event.target.value);
              setStatusUpdateMessage(null);
            }}
          />
          {statusSearchKeyword && (
            <button
              type="button"
              className="rmp-hold-clear"
              onClick={() => {
                setStatusSearchKeyword('');
                setStatusUpdateMessage(null);
              }}
              aria-label="ล้างคำค้นหา"
            >
              ×
            </button>
          )}
        </div>

        <div className="rmp-hold-results">
            {visibleStatusPlayers.length === 0 && (
              <div className="rmp-hold-empty">
                {statusSearchKeyword.trim()
                  ? 'ไม่พบชื่อผู้เล่น'
                  : 'ยังไม่มีผู้เล่นสถานะ HOLD'}
              </div>
            )}

            {visibleStatusPlayers.map(player => {
              const currentStatus = getStatus(player);
              const canSetHold = currentStatus === 'ACTIVE';
              const canSetActive = currentStatus === 'HOLD';
              const lockedPartner = getLockedPartner(player);

              return (
                <div className="rmp-hold-player" key={player.user_id}>
                  <div className="rmp-hold-player-info">
                    <strong>{player.name}</strong>
                    <span>
                      {normalizePlayerRank(player.level_rank, player.point)} • {Number(player.point || 0)} คะแนน
                    </span>
                    {lockedPartner && (
                      <small>🔒 Locked Pair: {lockedPartner.name}</small>
                    )}
                  </div>

                  <span className={`rmp-hold-current status-${currentStatus.toLowerCase()}`}>
                    {currentStatus}
                  </span>

                  {canSetHold && (
                    <button
                      type="button"
                      className="rmp-set-hold"
                      disabled={statusUpdatingId === Number(player.user_id)}
                      onClick={() => updateAvailabilityStatus(player, 'HOLD')}
                    >
                      {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '⏸ HOLD'}
                    </button>
                  )}

                  {canSetActive && (
                    <button
                      type="button"
                      className="rmp-set-active"
                      disabled={statusUpdatingId === Number(player.user_id)}
                      onClick={() => updateAvailabilityStatus(player, 'ACTIVE')}
                    >
                      {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '▶ ACTIVE'}
                    </button>
                  )}

                  {!canSetHold && !canSetActive && (
                    <small className="rmp-hold-blocked">
                      ต้องออกจาก {currentStatus} ก่อน
                    </small>
                  )}
                </div>
              );
            })}
        </div>

        {statusUpdateMessage && (
          <div className={`rmp-hold-message ${statusUpdateMessage.type}`} role="status">
            {statusUpdateMessage.text}
          </div>
        )}
      </section>

      {mode === 'PICK_ONE' && (
        <div className="rmp-pick-one-layout">
          <div className="rmp-field rmp-pick-anchor">
            <label>ผู้เล่นหลักที่ต้องอยู่ใน Match</label>
            <PlayerSearchDropdown
              value={pickedPlayer}
              allPlayers={pickOneAnchorPlayers}
              usedIds={[]}
              sortMode="ZERO_GAMES_WAIT"
              onChange={player => {
                const playerStatus = getStatus(player);

                if (playerStatus === 'HOLD') {
                  setError(`${player.name} อยู่สถานะ HOLD และยังไม่พร้อมลงเล่น`);
                  return;
                }

                if (playerStatus !== 'ACTIVE' && !approvePlaying) {
                  setError(
                    `${player.name} อยู่สถานะ ${playerStatus} — กรุณาเปิด Approve ทุกสถานะก่อนเลือก`
                  );
                  return;
                }

                setPickedPlayer(player);
                setPickOnePlayers([]);
                setNearbySearchKeyword('');
                setLastResult(null);
                setError('');
                setAiRecommendation(null);
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
              }}
            />

            {pickedPlayer && (
              <div className="rmp-pick-anchor-summary">
                <strong>{pickedPlayer.name}</strong>
                <span>
                  Rank {normalizePlayerRank(
                    pickedPlayer.level_rank,
                    pickedPlayer.point
                  ) || '-'}
                </span>
                <span>{Number(pickedPlayer.point || 0)} คะแนน</span>
                <span className="rmp-pick-position">#1 • Team 1</span>
                {pickedLockedPartner && (
                  <span className="locked">
                    #2 • Team 1 • 🔒 คู่ล็อก {pickedLockedPartner.name}
                  </span>
                )}
              </div>
            )}
          </div>

          {pickedPlayer && (
            <div className="rmp-pick-nearby-panel">
              <div className="rmp-pick-nearby-heading">
                  <div>
                    <strong>ผู้เล่น Rank ใกล้เคียง</strong>
                    <small>
                      แสดงทุกสถานะ • ACTIVE, QUEUE, PLAY, HOLD, CHECKOUT
                      {' '}• ภายในสถานะเรียงจำนวนเกม แล้วพิจารณา Rank และคะแนนใกล้เคียง
                    </small>
                </div>
                <span>
                  เลือกเพิ่ม {pickOnePlayers.length}/
                  {Math.max(0, 4 - 1 - (pickedLockedPartner ? 1 : 0))}
                </span>
              </div>

              <div className="rmp-pick-nearby-controls">
                <div className="rmp-pick-nearby-search">
                  <span aria-hidden="true">🔎</span>
                  <input
                    type="search"
                    value={nearbySearchKeyword}
                    placeholder="ค้นหาชื่อผู้เล่นทุก Rank"
                    onChange={event => setNearbySearchKeyword(event.target.value)}
                  />
                  {nearbySearchKeyword && (
                    <button
                      type="button"
                      onClick={() => setNearbySearchKeyword('')}
                      aria-label="ล้างคำค้นหาผู้เล่น Rank ใกล้เคียง"
                    >
                      ×
                    </button>
                  )}
                </div>
                <label className="rmp-pick-nearby-sort">
                  <span>จำนวนเกม</span>
                  <select
                    value={nearbyGameSort}
                    onChange={event => setNearbyGameSort(event.target.value)}
                    aria-label="เรียงผู้เล่นตามจำนวนเกม"
                  >
                    <option value="ASC">น้อย → มาก</option>
                    <option value="DESC">มาก → น้อย</option>
                  </select>
                </label>
              </div>

              <div className="rmp-pick-nearby-scroll">
                {pickOneNearbyPlayers.length === 0 && (
                  <div className="rmp-pick-nearby-empty">
                    {nearbySearchKeyword.trim()
                      ? 'ไม่พบชื่อผู้เล่น'
                      : 'ไม่พบผู้เล่น'}
                  </div>
                )}

                {pickOneNearbyPlayers.map(player => {
                  const selected = pickOnePlayers.some(
                    item => Number(item.user_id) === Number(player.user_id)
                  );
                  const rankDistance = getRankDistance(pickedPlayer, player);
                  const pointGap = Math.abs(
                    Number(player.point || 0) - Number(pickedPlayer.point || 0)
                  );
                  const lockedPartner = getLockedPartner(player);
                  const teammateCount = getRelationCount(
                    pairMap,
                    pickedPlayer,
                    player
                  );
                  const opponentCount = getRelationCount(
                    opponentMap,
                    pickedPlayer,
                    player
                  );
                  const holdBlocked =
                    getStatus(player) === 'HOLD' ||
                    getStatus(lockedPartner) === 'HOLD';
                  const selectedIndex = pickOneOrderedPlayers.findIndex(
                    item => Number(item.user_id) === Number(player.user_id)
                  );

                  return (
                    <button
                      type="button"
                      key={player.user_id}
                      className={`rmp-pick-nearby-card ${selected ? 'selected' : ''}`}
                      aria-pressed={selected}
                      disabled={holdBlocked}
                      onClick={() => togglePickOneNearbyPlayer(player)}
                    >
                      <span className="rmp-pick-nearby-name">{player.name}</span>
                      <span className="rmp-pick-nearby-meta">
                        <b>
                          {normalizePlayerRank(player.level_rank, player.point) || '-'}
                        </b>
                        <em>{Number(player.point || 0)} คะแนน</em>
                        <em className="rmp-pick-games">
                          🎮 {getPlayerGames(player)} เกม
                        </em>
                        <i className={`rmp-pick-status status-${getStatus(player).toLowerCase()}`}>
                          {getStatus(player) || '-'}
                        </i>
                      </span>
                      <small>
                        {rankDistance === 0
                          ? 'Rank เดียวกัน'
                          : `Rank ห่าง ${rankDistance} ระดับ`} • คะแนนห่าง {pointGap}
                      </small>
                      <span className="rmp-pick-history">
                        <b title="จำนวนครั้งที่เคยอยู่ทีมเดียวกับผู้เล่นหลัก">
                          👥 คู่ทีม {teammateCount}
                        </b>
                        <b title="จำนวนครั้งที่เคยอยู่ฝั่งตรงข้ามกับผู้เล่นหลัก">
                          ⚔ ตรงข้าม {opponentCount}
                        </b>
                      </span>
                      {lockedPartner && (
                        <small className="locked">🔒 Locked Pair: {lockedPartner.name}</small>
                      )}
                      <span className="rmp-pick-nearby-action">
                        {holdBlocked
                          ? '⏸ ผู้เล่นหรือคู่ล็อกอยู่ HOLD'
                          : selected
                          ? `✓ #${selectedIndex + 1} • Team ${selectedIndex < 2 ? 1 : 2}`
                          : '+ เลือก'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="rmp-pick-nearby-footer">
                <span>
                  แสดงทั้งหมด {pickOneNearbyCandidates.length} คน
                </span>
              </div>
              <div className="rmp-pick-team-rule">
                <strong>ลำดับ 1–2 = Team 1</strong>
                <span>ลำดับ 3–4 = Team 2</span>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'PRIORITY_SELECT' && (
        <div className="rmp-priority-select">
          <div className="rmp-priority-list-heading">
            <div>
              <strong>
                {priority === 'LEAST_GAMES'
                  ? '🎮 ผู้เล่นที่เล่นน้อยที่สุด'
                  : '⏱ ผู้เล่นที่รอนานที่สุด'}
              </strong>
              <small>
                ระบบเรียงตามอันดับคิว: สองอันดับแรกเป็น Team 1
                และสองอันดับถัดไปเป็น Team 2 ไม่อิงลำดับการกด
              </small>
            </div>
            <span className={selectedManualPlayers.length === 4 ? 'complete' : ''}>
              เลือกแล้ว {selectedManualPlayers.length}/4
            </span>
          </div>

          <input
            type="search"
            className="rmp-priority-search"
            value={priorityKeyword}
            placeholder="ค้นหาชื่อ คะแนน หรือระดับมือ"
            onChange={event => setPriorityKeyword(event.target.value)}
          />

          <div className="rmp-priority-player-list">
            {priorityPlayers.length === 0 && (
              <div className="rmp-priority-empty">ไม่พบผู้เล่น ACTIVE</div>
            )}

            {priorityPlayers.map((player, index) => {
              const selectedIndex = orderedPrioritySelections.findIndex(
                item => Number(item.user_id) === Number(player.user_id)
              );
              const selected = selectedIndex >= 0;
              const selectedTeam = selectedIndex < 2 ? 1 : 2;

              return (
                <button
                  type="button"
                  key={player.user_id}
                  className={[
                    'rmp-priority-player',
                    selected ? 'selected' : '',
                    selected ? `team-${selectedTeam}` : ''
                  ].filter(Boolean).join(' ')}
                  aria-pressed={selected}
                  onClick={() => togglePriorityPlayer(player)}
                >
                  <span className="rmp-priority-order">{index + 1}</span>
                  <span className="rmp-priority-name">
                    <strong>{player.name}</strong>
                    <small>
                      {player.level_rank || '-'} • {Number(player.point || 0)} คะแนน
                      {' • '}{getPlayStyleLabel(player.daily_play_style)}
                    </small>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>เวลารอ</small>
                    <strong>{formatWaitTime(player, now)}</strong>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>จำนวนเกม</small>
                    <strong>{getPlayerGames(player)} เกม</strong>
                  </span>
                  <span className="rmp-priority-check">
                    {selected
                      ? `✓ T${selectedTeam} • คนที่ ${selectedIndex + 1}`
                      : 'เลือก'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode === 'MANUAL_4' && (
        <div className="rmp-field">
          <label>เลือกผู้เล่นแยกตามทีม</label>
          <div className="rmp-manual-teams">
            <div className="rmp-manual-team rmp-manual-team-one">
              <div className="rmp-manual-team-heading">
                <strong>Team 1</strong>
                <small>คนที่ 1 และคนที่ 2 อยู่ทีมเดียวกัน</small>
              </div>

              {[0, 1].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={player}
                    allPlayers={orderedSelectablePlayers}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>

            <span className="rmp-manual-vs">VS</span>

            <div className="rmp-manual-team rmp-manual-team-two">
              <div className="rmp-manual-team-heading">
                <strong>Team 2</strong>
                <small>ผู้เล่นฝั่งตรงข้าม</small>
              </div>

              {[2, 3].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={player}
                    allPlayers={orderedSelectablePlayers}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rmp-error" role="alert">
          ❌ {error}
        </div>
      )}

      <button
        type="button"
        className={`rmp-generate-button ${
          mode === 'PRIORITY_SELECT' &&
          selectedManualPlayers.length > 0 &&
          selectedManualPlayers.length < 4
            ? 'random-fill'
            : ''
        }`}
        disabled={
          loading ||
          aiAutoLoading ||
          (
            (mode === 'AI_RANDOM' || mode === 'PICK_ONE') &&
            aiAutoEnabled
          ) ||
          lacksRequiredStylePlayers ||
          (AUTOMATIC_MODES.has(mode) &&
            activePlayers.length < requiredPlayerCount)
        }
        onClick={generateQueue}
      >
        {getGenerateButtonLabel()}
      </button>

      {lastResult && (
        <div className="rmp-result">
          <div className="rmp-result-header">
            <strong>
              สร้าง {createdMatches.length} Match สำเร็จ
            </strong>
            <span>
              Match #{createdMatches
                .map(item => item.groupMatchNo || item.matchId)
                .join(', #')}
            </span>
          </div>

          {lastResult.approvedPlayingPlayers?.length > 0 && (
            <div className="rmp-approved-result">
              Approved สถานะอื่น:{' '}
              {lastResult.approvedPlayingPlayers
                .map(player => `${player.name} (${player.status || '-'})`)
                .join(', ')}
            </div>
          )}

          <div className="rmp-result-list">
            {createdMatches.map((result, index) => (
              <article className="rmp-result-match" key={result.matchId || index}>
                <div className="rmp-result-match-heading">
                  <strong>
                    Match #{result.groupMatchNo || result.matchId || index + 1}
                  </strong>
                  <span>Team gap {result.analysis?.teamGap ?? '-'}</span>
                </div>

                <details className="rmp-result-details">
                  <summary>ดูรายละเอียดการจัด Match</summary>
                  <div className="rmp-result-details-body">
                {result.mode !== 'MANUAL_4' && result.analysis && (
                  <div className="rmp-range-result">
                    ลำดับคัดเลือก:{' '}
                    {result.analysis.sameRank
                      ? `Rank ${result.analysis.ranks?.[0] || '-'} เดียวกัน`
                      : `ขยาย Rank ห่าง ${result.analysis.rankDistance ?? '-'} ระดับ`}
                    {' • '}ช่วงคะแนนที่ใช้ไม่เกิน{' '}
                    {result.analysis.pointExpansionStep ?? 300} คะแนน
                    {' • '}คะแนนสูงสุด–ต่ำสุดห่าง{' '}
                    {result.analysis.playerSpread ?? '-'} คะแนน
                  </div>
                )}

                {result.opponentRepeatRelaxed && (
                  <div className="rmp-repeat-result relaxed">
                    ผ่อนคู่ต่อสู้ซ้ำจาก Match ล่าสุดอัตโนมัติ
                    เพราะไม่พบตัวเลือกใหม่ที่ผ่านเงื่อนไข
                  </div>
                )}

                {result.rangeExpandedForRepeatAvoidance && (
                  <div className="rmp-repeat-result relaxed">
                    ขยายคะแนน/Rank อัตโนมัติเพื่อหลีกเลี่ยงผู้เล่นซ้ำ
                    โดยใช้ช่วงใกล้ที่สุด {result.effectivePointRange || result.analysis?.pointExpansionStep || '-'} คะแนน
                  </div>
                )}

                {result.partnerRepeatOverridden && (
                  <div className="rmp-repeat-result approved">
                    ผู้ใช้ Approve คู่ทีมที่เคยเล่นด้วยกันแล้ว
                  </div>
                )}

                {result.analysis?.waitReleasedPartnerPairs?.length > 0 && (
                  <div className="rmp-repeat-result waited">
                    อนุญาตคู่ทีมเดิมเพราะผู้เล่นทั้งคู่รอครบ 30 นาที:{' '}
                    {result.analysis.waitReleasedPartnerPairs
                      .map(pair => `${pair.nameA} + ${pair.nameB}`)
                      .join(', ')}
                  </div>
                )}

                {result.mode === 'POINT_RANGE_RANDOM' && (
                  <div className="rmp-range-result">
                    ผู้เล่นอ้างอิง: {result.anchorPlayer?.name || '-'}{' '}
                    ({result.anchorPlayer?.point ?? '-'} คะแนน) • ช่วงไม่เกิน{' '}
                    {result.pointRange} คะแนน
                  </div>
                )}

                {(
                  result.mode === 'PICK_ONE' ||
                  result.mode === 'PRIORITY_SELECT'
                ) && (
                  <div className="rmp-range-result">
                    เทียบรายบุคคล:{' '}
                    {result.team1?.[0]?.name || 'T1-1'} ↔{' '}
                    {result.team2?.[0]?.name || 'T2-1'} ห่าง{' '}
                    {result.analysis?.individualGaps?.[0] ?? '-'} คะแนน
                    {' • '}
                    {result.team1?.[1]?.name || 'T1-2'} ↔{' '}
                    {result.team2?.[1]?.name || 'T2-2'} ห่าง{' '}
                    {result.analysis?.individualGaps?.[1] ?? '-'} คะแนน
                    {' • '}
                    ภายในทีม: T1 ห่าง{' '}
                    {result.analysis?.teammateGaps?.[0] ?? '-'} คะแนน
                    {' / '}T2 ห่าง{' '}
                    {result.analysis?.teammateGaps?.[1] ?? '-'} คะแนน
                    {' • '}
                    {result.rangeExpandedForRepeatAvoidance
                      ? `ระบบขยายอัตโนมัติเพื่อเลี่ยงคนซ้ำ (เดิมกำหนดไม่เกิน ${result.pointRange})`
                      : result.rangeOverridden
                        ? `ผู้ใช้ยืนยันใช้คู่ที่ใกล้ที่สุด (เดิมกำหนดไม่เกิน ${result.pointRange})`
                        : `เกณฑ์ไม่เกิน ${result.pointRange}`}
                  </div>
                )}

                {result.mode === 'PRIORITY_SELECT' && (
                  <div className="rmp-queue-team-order">
                    เรียงตามคิว: Team 1 ={' '}
                    {(result.team1 || []).map(player => player.name).join(' + ')}
                    {' • '}Team 2 ={' '}
                    {(result.team2 || []).map(player => player.name).join(' + ')}
                  </div>
                )}

                {result.playStyle && result.playStyle !== 'ANY' && (
                  <div className="rmp-result-play-style">
                    Mood ที่ใช้จัด Match: {result.playStyleLabel ||
                      getPlayStyleLabel(result.playStyle)}
                  </div>
                )}

                    <p className="rmp-reason">{result.reason}</p>
                  </div>
                </details>

                <div className="rmp-result-teams">
                  {renderTeam('Team 1', result.team1 || [])}
                  <span className="rmp-vs">VS</span>
                  {renderTeam('Team 2', result.team2 || [])}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default RandomMatchPanel;
